import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { FBoxContainer, FHButton, FHInputField, FHPageLink } from '../../components/base';
import { facilityActions } from '../../store';
import { history } from '../../helpers';
import PageContainer from '../../components/layout/PageContainer';
import { STANDARD_ERROR_MESSAGES } from '../../constants';

import type { ChangeEvent } from 'react';

function AddFacilityForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { required, invalidValue } = STANDARD_ERROR_MESSAGES;

  const facilityValidationSchema = Yup.object().shape({
    label: Yup.string().trim().required(required),
    tin: Yup.string().trim().required(required),
    npi: Yup.string().trim().required(required),
    group_medicaid_id: Yup.string().optional().notRequired(),
    street_address_line_one: Yup.string().trim().required(required),
    street_address_line_two: Yup.string(),
    street_address_state: Yup.string().trim().required(required),
    street_address_city: Yup.string().trim().required(required),
    street_address_zip: Yup.number()
      .typeError(invalidValue)
      .positive(invalidValue)
      .integer(invalidValue)
      .required(required),
  });

  const formik = useFormik({
    initialValues: {
      label: '',
      tin: '',
      npi: '',
      group_medicaid_id: '',
      street_address_line_one: '',
      street_address_line_two: '',
      street_address_state: '',
      street_address_city: '',
      street_address_zip: '',
    },
    onSubmit: async (values: any) => {
      const { error } = await dispatch(
        facilityActions.createFacility({
          data: values,
          getAccessTokenSilently,
        }),
      );
      if (!error) {
        history.navigate('/admin');
      }
    },
    validationSchema: facilityValidationSchema,
  });

  const { values, setFieldValue, submitForm, errors, touched } = formik;

  const handleUpdateFieldValues = (key: string, value: any) => {
    setFieldValue(key, value);
  };

  return (
    <PageContainer>
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer
          sx={{ borderRadius: '20px', boxShadow: 'none' }}
          dataTestId="addFacilityForm"
        >
          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <Grid container>
              <Grid item xs={12} md={10} lg={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography textAlign="left" variant="h4">
                      Facility Form
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>Label: </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['label']}
                        data-testid="labelField"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('label', e.target.value)
                        }
                        error={touched.label && Boolean(errors.label)}
                        helperText={touched.label && errors.label?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>TIN:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['tin']}
                        data-testid="tinField"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('tin', e.target.value)
                        }
                        error={touched.tin && Boolean(errors.tin)}
                        helperText={touched.tin && errors.tin?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>NPI:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['npi']}
                        data-testid="npiField"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('npi', e.target.value)
                        }
                        error={touched.npi && Boolean(errors.npi)}
                        helperText={touched.npi && errors.npi?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>Group Medicaid Id</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['group_medicaid_id']}
                        data-testid="groupMedicaidId"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('group_medicaid_id', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>Street Address 1:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['street_address_line_one']}
                        data-testid="streetAddress1Field"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_one', e.target.value)
                        }
                        error={
                          touched.street_address_line_one && Boolean(errors.street_address_line_one)
                        }
                        helperText={
                          touched.street_address_line_one &&
                          errors.street_address_line_one?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>Street Address 2:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['street_address_line_two']}
                        data-testid="streetAddress2Field"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_two', e.target.value)
                        }
                        error={
                          touched.street_address_line_two && Boolean(errors.street_address_line_two)
                        }
                        helperText={
                          touched.street_address_line_two &&
                          errors.street_address_line_two?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>City:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['street_address_city']}
                        data-testid="cityField"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_city', e.target.value)
                        }
                        error={touched.street_address_city && Boolean(errors.street_address_city)}
                        helperText={
                          touched.street_address_city && errors.street_address_city?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>State:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['street_address_state']}
                        data-testid="stateField"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_state', e.target.value)
                        }
                        error={touched.street_address_state && Boolean(errors.street_address_state)}
                        helperText={
                          touched.street_address_state && errors.street_address_state?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign={'left'}>Zip:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        value={values['street_address_zip']}
                        data-testid="zipField"
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_zip', e.target.value)
                        }
                        error={touched.street_address_zip && Boolean(errors.street_address_zip)}
                        helperText={
                          touched.street_address_zip && errors.street_address_zip?.toString()
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box ml={1} mt={3} sx={{ display: 'flex' }}>
                  <FHButton data-testid="submitButton" onClick={submitForm}>
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default AddFacilityForm;
